import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, X, Info } from 'lucide-react';

type Tone = 'info' | 'ok' | 'warn' | 'danger';
interface Toast {
  id: number;
  tone: Tone;
  title: string;
  body?: string;
}

interface Ctx {
  push: (t: Omit<Toast, 'id'>) => void;
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  info: (title: string, body?: string) => void;
  warn: (title: string, body?: string) => void;
}

const ToastCtx = createContext<Ctx>({
  push: () => {},
  success: () => {},
  error: () => {},
  info: () => {},
  warn: () => {},
});

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = nextId++;
    setToasts((cur) => [...cur, { ...t, id }]);
    setTimeout(() => setToasts((cur) => cur.filter((x) => x.id !== id)), 4000);
  }, []);

  const ctx: Ctx = {
    push,
    success: (title, body) => push({ tone: 'ok', title, body }),
    error: (title, body) => push({ tone: 'danger', title, body }),
    info: (title, body) => push({ tone: 'info', title, body }),
    warn: (title, body) => push({ tone: 'warn', title, body }),
  };

  return (
    <ToastCtx.Provider value={ctx}>
      {children}
      <div
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 100,
          maxWidth: 360,
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className="card"
            style={{
              padding: '0.75rem 0.9rem',
              boxShadow: 'var(--shadow-lg)',
              borderColor:
                t.tone === 'ok'
                  ? 'rgba(34,197,94,0.4)'
                  : t.tone === 'warn'
                    ? 'rgba(245,158,11,0.4)'
                    : t.tone === 'danger'
                      ? 'rgba(239,68,68,0.4)'
                      : undefined,
            }}
          >
            <div className="row">
              {t.tone === 'ok' && <CheckCircle2 size={16} className="ok" />}
              {t.tone === 'warn' && <AlertTriangle size={16} className="warn" />}
              {t.tone === 'danger' && <XCircle size={16} className="err" />}
              {t.tone === 'info' && <Info size={16} className="muted" />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{t.title}</div>
                {t.body && (
                  <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>
                    {t.body}
                  </div>
                )}
              </div>
              <button
                className="ghost"
                onClick={() => setToasts((cur) => cur.filter((x) => x.id !== t.id))}
                style={{ padding: 0, height: 20 }}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}
